import './notifications.style.css'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { UserAction as ApiCall } from '../../redux/action'
import { SVGImageComponentReact } from '../../themes/Images'
import TextBase from '../../common/text'
import { translate } from '../../helper'
import NotificationCard from './NotificationCard'
import { useNavigate } from 'react-router-dom'
import CustomSpinner from '../../common/CustomSpinner'
import { Button } from '@mui/material'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Utils from '../../utils'
import PageTitleHeader from '../../common/page-title-header'

const NotificationsScreen = (props) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { notifications } = useSelector((state) => state.User)
    const [lastElement, setLastElement] = useState(null)
    const [isFetching, setIsFetching] = useState(true)      // initial load
    const [isFetchingMore, setIsFetchingMore] = useState(false) // pagination
    const [isLoadingMarkAll, setIsLoadingMarkAll] = useState(false)

    const label = translate(props.isVendorNotes ? 'vendorNotesNotifications' : 'orderNotifications')
    const [page, setPage] = useState(1)

    const getData = (queryParams, append = false) => {
        if (append) {
            setIsFetchingMore(true);
        } else {
            setIsFetching(true);
        }

        dispatch(ApiCall.getNotifications(queryParams, append))
            .finally(() => {
                setIsFetching(false);
                setIsFetchingMore(false);
            });
    };

    useEffect(() => {
        getData(
            { vendor_notes: props.isVendorNotes, page, page_size: 20 },
            page !== 1
        );
    }, [page]);

    useEffect(() => {
        if (!lastElement) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && notifications?.page?.has_next && !isFetchingMore) {
                setPage((prev) => prev + 1);
            }
        });
        observer.observe(lastElement);
        return () => observer.disconnect();
    }, [lastElement, notifications?.page?.has_next, isFetchingMore]);

    const markAsReadAll = async () => {
        setIsLoadingMarkAll(true)
        try {
            await dispatch(ApiCall.markAsReadAll({ vendor_notes: props.isVendorNotes }))
            Utils.showAlert(1, translate('markAllAsRead'))
        } catch (error) {
            console.error("Failed to mark all as read", error)
            Utils.showAlert(2, translate('failedToMarkAll'))
        } finally {
            setIsLoadingMarkAll(false)
        }
    }

    const unreadCount = props.isVendorNotes
        ? notifications?.unread_vendornote_count
        : notifications?.unread_count;

    return (
        <div className="notifications-wrapper">
            {/* Premium Sticky Header */}
            <div className="notifications-header">
                <div className="notifications-header-inner">
                    <div className="notifications-header-left">
                        <span
                            className="back-button"
                            onClick={() => navigate(-1)}
                        >
                            <SVGImageComponentReact.IconArrowBack width={'var(--fs-lg)'} height={'var(--fs-lg)'} style={{ color: '#1A5A78' }} />
                        </span>
                        <div className="title-group">
                            <PageTitleHeader
                                                title={label}
                                                showBar={false}
                                                titleStyle={{ fontSize: 'calc(var(--fs-xl) * 1.4150943)', fontWeight: 600 }}
                                            />
                            {unreadCount > 0 && (
                                <span className="unread-badge">
                                    {unreadCount > 99 ? '99+' : unreadCount} unread
                                </span>
                            )}
                        </div>
                    </div>

                    {notifications?.data?.length > 0 && (
                        <Button
                            variant="contained"
                            disableElevation
                            size="small"
                            onClick={markAsReadAll}
                            disabled={isLoadingMarkAll || unreadCount === 0}
                            startIcon={
                                isLoadingMarkAll ? (
                                    <CustomSpinner size={'calc(var(--fs-xs)*1.886792)'} />
                                ) : (
                                    <CheckCircleOutlineIcon sx={{ color: '#fff', fontSize: 'calc(var(--fs-xs)*1.886792) !important' }} />
                                )
                            }
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: 'calc(var(--fs-sm2) * 1.015)',
                                borderRadius: 'calc(var(--sp-md2) * 1.2)',
                                px: 'calc(var(--sp-md2)*1.698113)',
                                py: 'calc(var(--sp-md2)*0.60377)',
                                backgroundColor: '#111827',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                letterSpacing: '0.01em',
                                boxShadow: '0 2px 12px rgba(17, 24, 39, 0.25)',
                                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    backgroundColor: '#1f2937',
                                    boxShadow: '0 6px 20px rgba(17, 24, 39, 0.35)',
                                    transform: 'translateY(-1px)',
                                },
                                '&:active': {
                                    transform: 'translateY(0)',
                                    boxShadow: '0 2px 8px rgba(17, 24, 39, 0.25)',
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: '#d1d5db',
                                    color: '#9ca3af',
                                    cursor: 'not-allowed',
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            {isLoadingMarkAll ? 'Marking...' : translate('markAllAsRead')}
                        </Button>
                    )}
                </div>
            </div>

            {/* Notification List */}
            <div className="scrollable-div">
                {!isFetching ? (
                    notifications?.data?.length > 0 ? (
                        <div className="notifications-list">
                            {notifications?.data?.map((notification, index) => (
                                <NotificationCard
                                    index={index}
                                    key={notification.id}
                                    notification={notification}
                                    setLastElement={
                                        index === notifications.data.length - 1 ? setLastElement : () => null
                                    }
                                />
                            ))}
                            {isFetchingMore && (
                                <div className="loading-more">
                                    <CustomSpinner size={'calc(var(--fs-xl)*1.886792)'} />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <div className="empty-state-icon-wrapper">
                                <NotificationsNoneIcon sx={{ fontSize: 'calc(var(--fs-hero) * 1.2)', color: '#d1d5db' }} />
                            </div>
                            <TextBase
                                size={'var(--fs-md2)'}
                                color={'#9CA3AF'}
                                fontWeight={500}
                            >
                                noNotifications
                            </TextBase>
                        </div>
                    )
                ) : (
                    <CustomSpinner fullPage />
                )}
            </div>
        </div>
    )
}

const Notifications = (props) => {
    return <NotificationsScreen {...props} isVendorNotes={false} />
}

export const VendorNotesNotifications = (props) => {
    return <NotificationsScreen {...props} isVendorNotes={true} />
}

export default Notifications